/**
 * 인증(Auth) 도메인 에러 — title/message는 **i18n 키 문자열**만 둡니다.
 * 번역은 컴포넌트 등 UI에서 `t(key)`(또는 토스트 스토어에 키 전달)로 처리합니다.
 */

import {
  getBackendI18nKeyFromResponse,
  getHttpStatus,
  getServerMessage,
  isNetworkError,
  looksLikeI18nKey,
  normalizeBackendI18nKey,
} from './apiError';
import type { ErrorMessage } from './types';

/**
 * 인증 관련 에러 메시지 상수
 */
export const AUTH_ERRORS = {
  // 입력 검증 에러
  EMAIL_REQUIRED: {
    title: 'auth:input_error',
    message: 'auth:email_required',
    type: 'error' as const,
  },
  EMAIL_INVALID: {
    title: 'auth:input_error',
    message: 'auth:email_invalid',
    type: 'error' as const,
  },
  PASSWORD_REQUIRED: {
    title: 'auth:input_error',
    message: 'auth:password_required',
    type: 'error' as const,
  },
  PASSWORD_WEAK: {
    title: 'auth:password_weak',
    message: 'auth:password_weak_message',
    type: 'warning' as const,
  },
  PASSWORD_MISMATCH: {
    title: 'auth:password_mismatch',
    message: 'auth:password_mismatch_message',
    type: 'error' as const,
  },
  NAME_REQUIRED: {
    title: 'auth:input_error',
    message: 'auth:name_required',
    type: 'error' as const,
  },
  NAME_TOO_SHORT: {
    title: 'auth:input_error',
    message: 'auth:name_too_short',
    type: 'error' as const,
  },

  // 이메일 허용/거부 에러
  EMAIL_NOT_ALLOWED: {
    title: 'auth:email_not_allowed',
    message: 'auth:email_not_allowed_message',
    type: 'error' as const,
  },
  EMAIL_PENDING_APPROVAL: {
    title: 'auth:email_pending_approval_title',
    message: 'auth:email_pending_approval',
    type: 'info' as const,
  },
  EMAIL_REJECTED: {
    title: 'auth:email_rejected',
    message: 'auth:email_rejected',
    type: 'error' as const,
  },
  EMAIL_ALREADY_REGISTERED: {
    title: 'auth:email_already_registered',
    message: 'auth:email_already_registered',
    type: 'info' as const,
  },

  // 이메일 인증 관련
  EMAIL_VERIFICATION_REQUIRED: {
    title: 'auth:email_verification_sent',
    message: 'auth:email_verification_sent',
    type: 'error' as const,
  },
  EMAIL_VERIFICATION_EXPIRED: {
    title: 'auth:verification_expired',
    message: 'auth:verification_expired',
    type: 'error' as const,
  },
  EMAIL_VERIFICATION_SENT: {
    title: 'auth:email_sent',
    message: 'auth:email_verification_sent',
    type: 'info' as const,
  },
  EMAIL_VERIFICATION_RESENT: {
    title: 'auth:email_sent',
    message: 'auth:email_verification_sent',
    type: 'info' as const,
  },
  EMAIL_VERIFICATION_ALREADY_SENT: {
    title: 'auth:email_already_sent',
    message: 'auth:email_already_sent',
    type: 'warning' as const,
  },
  EMAIL_VERIFICATION_COMPLETED: {
    title: 'auth:email_verified',
    message: 'auth:email_verified',
    type: 'success' as const,
  },

  // 회원가입 관련
  REGISTRATION_SUCCESS: {
    title: 'auth:registration_complete',
    message: 'auth:registration_complete',
    type: 'success' as const,
  },
  REGISTRATION_FAILED: {
    title: 'auth:registration_failed',
    message: 'auth:registration_failed',
    type: 'error' as const,
  },
  REQUEST_REGISTRATION_SUCCESS: {
    title: 'auth:registration_request_received',
    message: 'auth:registration_request_received',
    type: 'success' as const,
  },
  REQUEST_REGISTRATION_FAILED: {
    title: 'auth:registration_request_failed',
    message: 'auth:registration_request_failed',
    type: 'error' as const,
  },

  // 로그인 에러
  LOGIN_FAILED: {
    title: 'auth:login_failed',
    message: 'auth:login_failed_message',
    type: 'error' as const,
  },

  // 시스템 에러
  EMAIL_SEND_FAILED: {
    title: 'auth:email_send_failed',
    message: 'auth:email_send_failed_message',
    type: 'error' as const,
  },
  SERVER_ERROR: {
    title: 'auth:server_error',
    message: 'auth:server_error_message',
    type: 'error' as const,
  },
  NETWORK_ERROR: {
    title: 'auth:network_error',
    message: 'auth:network_error_message',
    type: 'error' as const,
  },
  UNKNOWN_ERROR: {
    title: 'auth:unknown_error',
    message: 'auth:unknown_error_message',
    type: 'error' as const,
  },
  RATE_LIMIT: {
    title: 'auth:rate_limit',
    message: 'auth:rate_limit_message',
    type: 'warning' as const,
  },
} as const;

function mapMessageHeuristics(lower: string): ErrorMessage | null {
  if (lower.includes('이메일') || lower.includes('email')) {
    if (lower.includes('인증') || lower.includes('verification')) {
      return AUTH_ERRORS.EMAIL_VERIFICATION_REQUIRED;
    }
    if (lower.includes('가입') || lower.includes('registered')) {
      return AUTH_ERRORS.EMAIL_ALREADY_REGISTERED;
    }
    return AUTH_ERRORS.EMAIL_INVALID;
  }

  if (lower.includes('비밀번호') || lower.includes('password')) {
    return AUTH_ERRORS.PASSWORD_WEAK;
  }

  if (lower.includes('가입') || lower.includes('registration')) {
    return AUTH_ERRORS.REGISTRATION_FAILED;
  }

  return null;
}

/**
 * API 에러를 **i18n 키만** 담은 ErrorMessage로 변환. 번역은 UI에서 `t(...)`로 수행.
 */
export function parseApiError(error: unknown): ErrorMessage {
  if (!error) {
    return AUTH_ERRORS.UNKNOWN_ERROR;
  }

  if (isNetworkError(error)) {
    return AUTH_ERRORS.NETWORK_ERROR;
  }

  const fromBackend = getBackendI18nKeyFromResponse(error);
  if (fromBackend) {
    if (looksLikeI18nKey(fromBackend)) {
      const messageKey = normalizeBackendI18nKey(fromBackend);
      return {
        title: AUTH_ERRORS.SERVER_ERROR.title,
        message: messageKey,
        type: 'error',
      };
    }
    const mapped = mapMessageHeuristics(fromBackend.toLowerCase());
    if (mapped) {
      return mapped;
    }
  }

  const message = getServerMessage(error);
  if (message) {
    const mapped = mapMessageHeuristics(message.toLowerCase());
    if (mapped) {
      return mapped;
    }
  }

  switch (getHttpStatus(error)) {
    case 400:
      return AUTH_ERRORS.EMAIL_INVALID;
    case 401:
      return AUTH_ERRORS.LOGIN_FAILED;
    case 403:
      return AUTH_ERRORS.EMAIL_NOT_ALLOWED;
    case 409:
      return AUTH_ERRORS.EMAIL_ALREADY_REGISTERED;
    case 429:
      return AUTH_ERRORS.RATE_LIMIT;
    case 500:
      return AUTH_ERRORS.SERVER_ERROR;
    default:
      return AUTH_ERRORS.UNKNOWN_ERROR;
  }
}
